/* global React */
// ResultsScene — horizontal 3-stop timeline
//
// The Results section is the honest "we don't have numbers yet, here's how
// we're earning them" section. A plain horizontal timeline with three
// milestones (Build → Live test → Publish), connected by a rule that the
// active phase highlights in gold.
//
// Restraint on purpose: this sits between the Privacy ledger and the FAQ,
// and shouldn't over-dramatize. The rhythm here is "measured plan," not
// "cinematic moment."

const { useEffect, useRef, useState } = React;

const PHASES = [
  {
    n: '01',
    label: 'Build',
    window: 'Weeks 1–4',
    state: 'complete',
    body: 'We connect Movie Minder to your box office, set up the on-screen QR, and get text messages flowing.',
    markers: ['Partner signed', 'Texts live', 'Reporting ready'],
  },
  {
    n: '02',
    label: 'Live test',
    window: '60 days',
    state: 'active',
    body: 'Real trailers on real screens, selling real tickets. Weekends and weeknights, every genre — no test data, no focus groups.',
    markers: ['Wave 01 live', 'Wave 02 rolling', 'Full theater rollout'],
  },
  {
    n: '03',
    label: 'Publish',
    window: 'Q3',
    state: 'upcoming',
    body: 'Tap-through rates, ticket sales, revenue per screening, return visits — all published right here, in plain sight.',
    markers: ['Numbers in', 'Partner sign-off', 'Results live'],
  },
];

function useInView(threshold = 0.2) {
  const ref = useRef(null);
  // Start visible immediately — the entrance animation is nice-to-have but
  // content must always be visible. If an IO is wired up and fires later,
  // that's additive. Cleaner: just be visible.
  const [inView, setInView] = useState(true);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    // Still wire up IO for first-enter animation; start as false then flip true
    // — but since we default to true, the transition plays from 0 to 1 on
    // the first render anyway via CSS. Nothing more to do here.
    return () => {};
  }, [threshold]);
  return { ref, inView };
}

function ResultsScene() {
  const { ref, inView } = useInView(0.05);

  return (
    <section className="mmx-res" id="proof" aria-labelledby="mmx-res-heading">
      <div className="mmx-res-inner" ref={ref}>
        {/* ---- HEAD ---- */}
        <header className="mmx-res-head">
          <span className="mmx-eyebrow">Results</span>
          <h2 id="mmx-res-heading" className="mmx-res-headline">
            A national rollout,
            <span className="mmx-res-headline-em"> measured every step of the way.</span>
          </h2>
          <p className="mmx-res-lead">
            We&#39;re not asking you to take our word for it. Movie Minder is rolling out
            in three clear steps, and every number from the live test will be
            published right here.
          </p>
        </header>

        {/* ---- TIMELINE ---- */}
        <div className={`mmx-res-timeline ${inView ? 'is-in' : ''}`}>
          {/* Section-scoped purple blooms floating behind the cards. Three
              drifting blobs positioned behind the 3 stops. Fade in with the
              timeline; very slow parallax-drift once visible. */}
          <div className="mmx-res-blooms" aria-hidden>
            <div className="mmx-res-bloom mmx-res-bloom-1" />
            <div className="mmx-res-bloom mmx-res-bloom-2" />
            <div className="mmx-res-bloom mmx-res-bloom-3" />
          </div>

          {/* Progress rule + labels + nodes — a single unified progress bar.
              The rule threads behind the labels. Nodes sit ON the rule. */}
          <div className="mmx-res-progress" aria-hidden>
            <div className="mmx-res-progress-track">
              <div className="mmx-res-progress-fill" />
            </div>
            <div className="mmx-res-progress-nodes">
              {PHASES.map((p) => (
                <div key={p.n} className={`mmx-res-progress-node is-${p.state}`}>
                  <span className="mmx-res-progress-node-inner" />
                  {p.state === 'active' && <span className="mmx-res-progress-node-halo" />}
                </div>
              ))}
            </div>
            <div className="mmx-res-progress-labels">
              {PHASES.map((p) => (
                <div key={p.n} className={`mmx-res-progress-cell is-${p.state}`}>
                  <span className="mmx-res-progress-window">{p.window}</span>
                  <span className="mmx-res-progress-label">{p.n} · {p.label}</span>
                </div>
              ))}
            </div>
          </div>

          <ol className="mmx-res-stops">
            {PHASES.map((p, i) => (
              <li
                key={p.n}
                className={`mmx-res-stop is-${p.state}`}
                style={{ '--i': i }}
              >
                {/* Detail card */}
                <div className="mmx-res-stop-card">
                  <span className="mmx-res-stop-card-glow" aria-hidden />
                  <div className="mmx-res-stop-head">
                    <span className="mmx-res-stop-n">{p.n}</span>
                    <h3 className="mmx-res-stop-label">{p.label}</h3>
                    <span className={`mmx-res-stop-state is-${p.state}`}>
                      {p.state === 'complete' && 'Done'}
                      {p.state === 'active' && 'In progress'}
                      {p.state === 'upcoming' && 'Next'}
                    </span>
                  </div>
                  <p className="mmx-res-stop-body">{p.body}</p>
                  <ul className="mmx-res-stop-markers">
                    {p.markers.map((m, mi) => {
                      // Within active phase: first marker done, second in-flight, third pending
                      let mstate = 'pending';
                      if (p.state === 'complete') mstate = 'done';
                      else if (p.state === 'active') {
                        if (mi === 0) mstate = 'done';
                        else if (mi === 1) mstate = 'inflight';
                      }
                      return (
                        <li key={m} className={`mmx-res-stop-marker is-${mstate}`}>
                          <span className="mmx-res-stop-marker-dot" />
                          <span>{m}</span>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </li>
            ))}
          </ol>
        </div>

        {/* ---- FOOT ---- */}
        <div className="mmx-res-foot">
          <div className="mmx-res-foot-k">Next milestone</div>
          <div className="mmx-res-foot-v">
            Live numbers publish after the Q3 pilot, right here, in plain sight:
            <span className="mmx-res-foot-metrics">
              Tap-through rate
              <span className="mmx-res-foot-sep">·</span>
              Advance tickets sold
              <span className="mmx-res-foot-sep">·</span>
              Revenue per 1,000 screenings
              <span className="mmx-res-foot-sep">·</span>
              Return visits
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}

window.MMXResults = ResultsScene;
